import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { blogPosts as blogPostsService } from "../../services/api";

// Blog post data interface
interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  author: {
    name: string;
    email: string;
  };
  status: "draft" | "published" | "archived";
  category: {
    id: number;
    name: string;
    slug: string;
  };
  tags: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  views_count: number;
  is_featured: boolean;
}

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface Author {
  id: number;
  name: string;
  email: string;
}

export default function BlogPosts() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedAuthor, setSelectedAuthor] = useState("All");
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Load data on component mount
  useEffect(() => {
    loadBlogPosts();
    loadCategories();
    loadAuthors();
  }, []);

  const loadBlogPosts = async () => {
    try {
      setLoading(true);
      const response = (await blogPostsService.list({
        page: 1,
        limit: 1000,
      })) as any;
      if (response && response.posts) {
        // Transform backend response to match TypeScript interface
        const transformedPosts = response.posts.map((post: any) => ({
          ...post,
          author: {
            name: post.author_name || post.author?.name || "Unknown",
            email: post.author_email || post.author?.email || "",
          },
          category: {
            id: post.category_id || post.category?.id || 0,
            name: post.category_name || post.category?.name || "Uncategorized",
            slug: post.category_slug || post.category?.slug || "",
          },
          tags: Array.isArray(post.tags) ? post.tags : [],
        }));
        setPosts(transformedPosts);
      }
    } catch (error) {
      console.error("Error loading blog posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = (await blogPostsService.getCategories()) as any;
      if (response && response.categories) {
        setCategories(response.categories);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const loadAuthors = async () => {
    try {
      const response = (await blogPostsService.getAuthors()) as any;
      if (response && response.authors) {
        setAuthors(response.authors);
      }
    } catch (error) {
      console.error("Error loading authors:", error);
    }
  };

  // Filter and sort blog posts
  const filteredAndSortedPosts = useMemo(() => {
    let filtered = posts.filter((post: BlogPost) => {
      const matchesSearch =
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.author.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some((tag: any) =>
          tag.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesCategory =
        selectedCategory === "All" || post.category.name === selectedCategory;
      const matchesStatus =
        selectedStatus === "All" || post.status === selectedStatus;
      const matchesAuthor =
        selectedAuthor === "All" || post.author.name === selectedAuthor;

      return matchesSearch && matchesCategory && matchesStatus && matchesAuthor;
    });

    // Sort posts
    filtered.sort((a: BlogPost, b: BlogPost) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case "title":
          aValue = a.title;
          bValue = b.title;
          break;
        case "author":
          aValue = a.author.name;
          bValue = b.author.name;
          break;
        case "status":
          aValue = a.status;
          bValue = b.status;
          break;
        case "views_count":
          aValue = a.views_count;
          bValue = b.views_count;
          break;
        case "published_at":
          aValue = a.published_at || "0000-00-00";
          bValue = b.published_at || "0000-00-00";
          break;
        default:
          aValue = a.updated_at;
          bValue = b.updated_at;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    });

    return filtered;
  }, [
    posts,
    searchTerm,
    selectedCategory,
    selectedStatus,
    selectedAuthor,
    sortBy,
    sortOrder,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedPosts.length / itemsPerPage);
  const paginatedPosts = filteredAndSortedPosts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "published":
        return "success";
      case "draft":
        return "warning";
      case "archived":
        return "secondary";
      default:
        return "primary";
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("ro-RO", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("All");
    setSelectedStatus("All");
    setSelectedAuthor("All");
    setCurrentPage(1);
  };

  const handleDelete = async (postId: number, postTitle: string) => {
    if (window.confirm(`Are you sure you want to delete "${postTitle}"?`)) {
      try {
        const response = (await blogPostsService.delete(postId)) as any;
        if (response && response.message) {
          setPosts(posts.filter((p) => p.id !== postId));
          alert("Post deleted successfully");
        } else {
          alert(
            "Failed to delete post: " + (response?.error || "Unknown error")
          );
        }
      } catch (error) {
        console.error("Error deleting post:", error);
        alert("Error deleting post");
      }
    }
  };

  if (loading) {
    return (
      <div className="page-layout">
        <div className="loading-spinner"></div>
        <p>Loading blog posts...</p>
      </div>
    );
  }

  return (
    <div className="page-layout">
      <div className="page-header">
        <h2 className="title">Blog Posts</h2>
        <div className="right">
          <button
            className="btn btn-primary btn-small"
            onClick={() => navigate("/account/blog-posts/create")}
          >
            <i className="fa-solid fa-plus"></i> Add New Post
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="blog-filters mt-4">
        <div className="filters-row">
          <div className="search-box">
            <i className="fa-solid fa-search"></i>
            <input
              type="text"
              placeholder="Search posts, authors, or tags..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="All">All Categories</option>
            {Array.isArray(categories) &&
              categories.map((category: Category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="All">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>

          <select
            value={selectedAuthor}
            onChange={(e) => {
              setSelectedAuthor(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="All">All Authors</option>
            {Array.isArray(authors) &&
              authors.map((author: Author) => (
                <option key={author.id} value={author.name}>
                  {author.name}
                </option>
              ))}
          </select>

          {(searchTerm ||
            selectedCategory !== "All" ||
            selectedStatus !== "All" ||
            selectedAuthor !== "All") && (
            <button className="btn btn-secondary btn-sm" onClick={clearFilters}>
              <i className="fa-solid fa-times"></i> Clear Filters
            </button>
          )}
        </div>

        <div className="results-info">
          <span>
            Showing {paginatedPosts.length} of {filteredAndSortedPosts.length}{" "}
            posts
          </span>
        </div>
      </div>

      {/* Table Section */}
      <div className="blog-table-container">
        <table className="blog-table">
          <thead>
            <tr>
              <th>
                <button
                  className={`sort-button ${
                    sortBy === "title" ? "active" : ""
                  }`}
                  onClick={() => handleSort("title")}
                >
                  Title
                  {sortBy === "title" && (
                    <i
                      className={`fa-solid fa-sort-${
                        sortOrder === "asc" ? "up" : "down"
                      }`}
                    ></i>
                  )}
                </button>
              </th>
              <th>
                <button
                  className={`sort-button ${
                    sortBy === "author" ? "active" : ""
                  }`}
                  onClick={() => handleSort("author")}
                >
                  Author
                  {sortBy === "author" && (
                    <i
                      className={`fa-solid fa-sort-${
                        sortOrder === "asc" ? "up" : "down"
                      }`}
                    ></i>
                  )}
                </button>
              </th>
              <th>
                <button
                  className={`sort-button ${
                    sortBy === "status" ? "active" : ""
                  }`}
                  onClick={() => handleSort("status")}
                >
                  Status
                  {sortBy === "status" && (
                    <i
                      className={`fa-solid fa-sort-${
                        sortOrder === "asc" ? "up" : "down"
                      }`}
                    ></i>
                  )}
                </button>
              </th>
              <th>Category</th>
              {/* <th>
                <button
                  className={`sort-button ${
                    sortBy === "views_count" ? "active" : ""
                  }`}
                  onClick={() => handleSort("views_count")}
                >
                  Views
                  {sortBy === "views_count" && (
                    <i
                      className={`fa-solid fa-sort-${
                        sortOrder === "asc" ? "up" : "down"
                      }`}
                    ></i>
                  )}
                </button>
              </th> */}
              <th>
                <button
                  className={`sort-button ${
                    sortBy === "published_at" ? "active" : ""
                  }`}
                  onClick={() => handleSort("published_at")}
                >
                  Published
                  {sortBy === "published_at" && (
                    <i
                      className={`fa-solid fa-sort-${
                        sortOrder === "asc" ? "up" : "down"
                      }`}
                    ></i>
                  )}
                </button>
              </th>
              <th>
                <button
                  className={`sort-button ${
                    sortBy === "updated_at" ? "active" : ""
                  }`}
                  onClick={() => handleSort("updated_at")}
                >
                  Updated
                  {sortBy === "updated_at" && (
                    <i
                      className={`fa-solid fa-sort-${
                        sortOrder === "asc" ? "up" : "down"
                      }`}
                    ></i>
                  )}
                </button>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedPosts.length > 0 ? (
              paginatedPosts.map((post: BlogPost) => (
                <tr key={post.id}>
                  <td className="title-cell">
                    <div className="post-title">
                      <strong>{post.title}</strong>
                      {parseInt(post.is_featured?.toString() || "0") === 1 && (
                        <i
                          className="fa-solid fa-star featured-icon"
                          title="Featured"
                        ></i>
                      )}
                    </div>
                    <div className="post-excerpt">{post.excerpt}</div>
                    <div className="post-tags">
                      {post.tags.map((tag: any) => (
                        <span key={tag.id} className="tag">
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>{post.author.name}</td>
                  <td>
                    <span
                      className={`status-badge status-${getStatusColor(
                        post.status
                      )}`}
                    >
                      {post.status.charAt(0).toUpperCase() +
                        post.status.slice(1)}
                    </span>
                  </td>
                  <td>{post.category.name}</td>
                  {/* <td>{post.views_count.toLocaleString()}</td> */}
                  <td>{formatDate(post.published_at)}</td>
                  <td>{formatDate(post.updated_at)}</td>
                  <td>
                    <div className="actions">
                      <button
                        className="btn btn-sm btn-primary"
                        title="Edit"
                        onClick={() =>
                          navigate(`/account/blog-posts/edit/${post.id}`)
                        }
                      >
                        <i className="fa-solid fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        title="View"
                        onClick={() =>
                          window.open(`/blog/${post.slug}`, "_blank")
                        }
                      >
                        <i className="fa-solid fa-eye"></i>
                      </button>
                      {/* <button
                        className="btn btn-sm btn-warning"
                        title="Duplicate"
                        onClick={() => {
                          alert("Duplicate functionality coming soon!");
                        }}
                      >
                        <i className="fa-solid fa-copy"></i>
                      </button> */}
                      <button
                        className="btn btn-sm"
                        style={{ color: "#dc3545" }}
                        title="Delete"
                        onClick={() => handleDelete(post.id, post.title)}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="no-results">
                  <div className="no-results-content">
                    <i className="fa-solid fa-search"></i>
                    <h3>No blog posts found</h3>
                    <p>Try adjusting your search criteria or filters.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination">
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              <i className="fa-solid fa-angles-left"></i>
            </button>
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <i className="fa-solid fa-angle-left"></i>
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum =
                Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              return (
                <button
                  key={pageNum}
                  className={`btn btn-sm ${
                    currentPage === pageNum ? "btn-primary" : ""
                  }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <i className="fa-solid fa-angle-right"></i>
            </button>
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              <i className="fa-solid fa-angles-right"></i>
            </button>
          </div>
          <div className="pagination-info">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}
    </div>
  );
}
